import { IPaginate, RootState, StoreState } from '@/types/types';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { MessagesManagementService } from '@/services/messages-management.service';

export interface IQueueDepthSample {
  ts: number;
  waiting: number;
  active: number;
  delayed: number;
  failed: number;
}

export interface IQueueStats {
  queue: string;
  paused: boolean;
  counts: { [state: string]: number };
  oldestWaitingJobId: string | number | null;
  oldestWaitingAgeMs: number;
  oldestActiveJobId: string | number | null;
  oldestActiveAgeMs: number;
  history: IQueueDepthSample[];
}

export interface IQueueJob {
  id: string | number;
  name: string;
  state: string;
  timestamp: number;
  ageMs: number;
  processedOn: number | null;
  finishedOn: number | null;
  attemptsMade: number;
  failedReason: string | null;
  transactionId: string | null;
  userId: number | null;
  messageCount: number | null;
  isGlobal: boolean;
  bulk: { batchId?: number; chunkId?: number } | null;
}

export interface IBulkBatch {
  id: number;
  transactionId: string;
  status: string;
  totalMessages: number;
  totalSegments: number;
  totalChunks: number;
  queuedChunks: number;
  completedChunks: number;
  failedChunks: number;
  refundedSegments: number;
  isGlobal: boolean;
  senderAddress: string;
  lastError: string;
  createdAt: string;
  userId: number;
  userName: string;
  userEmail: string;
}

export interface IBulkChunk {
  id: number;
  chunkIndex: number;
  jobId: string;
  status: string;
  messageCount: number;
  segments: number;
  lastError: string;
  updatedAt: string;
}

export interface IDeliveryLog {
  _id: string;
  createdAt: string;
  submittedDate?: string;
  consumerId: string | number;
  senderAddress: string;
  receiverAddress: string | string[];
  message: string;
  segments: number;
  deliveryStatus: string;
}

export interface ILogsFilterAccount {
  id: number;
  name: string;
  email: string;
  isSubAccount: boolean;
}

export interface ILogsFilters {
  mainAccounts: ILogsFilterAccount[];
  subAccounts: ILogsFilterAccount[];
  senderIds: string[];
}

const state: StoreState<IBulkBatch> = {
  all: [],
  paginate: {
    total: 0,
    page: 1,
    pages: 1,
    itemsPerPage: 15,
  },
  stats: null,
  jobs: [],
  jobsPaginate: {
    total: 0,
    page: 1,
    pages: 1,
    itemsPerPage: 15,
  },
  chunks: [],
  chunksPaginate: {
    total: 0,
    page: 1,
    pages: 1,
    itemsPerPage: 15,
  },
  logs: [],
  logsPaginate: {
    total: 0,
    page: 1,
    pages: 1,
    itemsPerPage: 15,
  },
  logsTotalSegments: 0,
  logsFilters: {
    mainAccounts: [],
    subAccounts: [],
    senderIds: [],
  },
};

const mutations: MutationTree<StoreState<IBulkBatch>> = {
  UPDATE_STATS(state, payload: IQueueStats) {
    state.stats = payload;
  },
  UPDATE_JOBS(state, payload: Array<IQueueJob>) {
    state.jobs = payload;
  },
  UPDATE_JOBS_PAGINATION(state, payload: IPaginate) {
    state.jobsPaginate = payload;
  },
  UPDATE_LIST(state, payload: Array<IBulkBatch>) {
    state.all = payload;
  },
  UPDATE_PAGINATION(state, payload: IPaginate) {
    state.paginate = payload;
  },
  UPDATE_CHUNKS(state, payload: Array<IBulkChunk>) {
    state.chunks = payload;
  },
  UPDATE_CHUNKS_PAGINATION(state, payload: IPaginate) {
    state.chunksPaginate = payload;
  },
  UPDATE_LOGS(state, payload: Array<IDeliveryLog>) {
    state.logs = payload;
  },
  UPDATE_LOGS_PAGINATION(state, payload: IPaginate) {
    state.logsPaginate = payload;
  },
  UPDATE_LOGS_TOTAL_SEGMENTS(state, payload: number) {
    state.logsTotalSegments = payload;
  },
  UPDATE_LOGS_FILTERS(state, payload: ILogsFilters) {
    state.logsFilters = payload;
  },
};

const actions: ActionTree<StoreState<IBulkBatch>, RootState> = {
  async queueStats({ commit, dispatch }) {
    try {
      const response$ = await MessagesManagementService.queueStats();
      commit('UPDATE_STATS', response$.data);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
    }
  },
  async listJobs({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await MessagesManagementService.jobs(query);
      dispatch('isPageLoading', false, { root: true });
      const { docs, page, total, limit: itemsPerPage } = response$.data;
      commit('UPDATE_JOBS', docs);
      commit('UPDATE_JOBS_PAGINATION', {
        page,
        total,
        itemsPerPage,
        pages: itemsPerPage > 0 ? Math.max(1, Math.ceil(total / itemsPerPage)) : 1,
      });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async retryJob({ dispatch }, payload: { jobId: string | number; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await MessagesManagementService.retryJob(payload.jobId);
      dispatch('isLoading', false, { root: true });
      dispatch('snackBarMessage', `Job requeued as ${response$.data?.newJobId}`, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('listJobs', payload.query);
      dispatch('queueStats');
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async removeJob({ dispatch }, payload: { jobId: string | number; query: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      await MessagesManagementService.removeJob(payload.jobId);
      dispatch('isLoading', false, { root: true });
      dispatch('snackBarMessage', `Job ${payload.jobId} removed from the queue`, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('listJobs', payload.query);
      dispatch('queueStats');
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async listBatches({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await MessagesManagementService.batches(query);
      dispatch('isPageLoading', false, { root: true });
      const { docs, page, pages, total, limit: itemsPerPage } = response$.data.paginateObj;
      commit('UPDATE_LIST', docs);
      commit('UPDATE_PAGINATION', { page, pages, total, itemsPerPage });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async listChunks({ commit, dispatch }, payload: { batchId: number; query: string }) {
    try {
      dispatch('isDialogLoading', true, { root: true });
      const response$ = await MessagesManagementService.batchChunks(payload.batchId, payload.query);
      dispatch('isDialogLoading', false, { root: true });
      const { docs, page, pages, total, limit: itemsPerPage } = response$.data.paginateObj;
      commit('UPDATE_CHUNKS', docs);
      commit('UPDATE_CHUNKS_PAGINATION', { page, pages, total, itemsPerPage });
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async resolveChunk(
    { dispatch },
    payload: {
      chunkId: number;
      action: 'refund' | 'mark-sent';
      batchId: number;
      chunksQuery: string;
      batchesQuery: string;
    },
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      await MessagesManagementService.resolveChunk(payload.chunkId, payload.action);
      dispatch('isLoading', false, { root: true });
      dispatch(
        'snackBarMessage',
        payload.action === 'refund' ? 'Chunk failed and reserved credits refunded' : 'Chunk confirmed as sent',
        { root: true },
      );
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('listChunks', { batchId: payload.batchId, query: payload.chunksQuery });
      dispatch('listBatches', payload.batchesQuery);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
};

const logsActions: ActionTree<StoreState<IBulkBatch>, RootState> = {
  async listLogs({ commit, dispatch }, query: string) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const response$ = await MessagesManagementService.logs(query);
      dispatch('isPageLoading', false, { root: true });
      const { paginateObj, totalSegments } = response$.data;
      const { docs, page, pages, total, limit: itemsPerPage } = paginateObj;
      commit('UPDATE_LOGS', docs);
      commit('UPDATE_LOGS_PAGINATION', { page, pages, total, itemsPerPage });
      commit('UPDATE_LOGS_TOTAL_SEGMENTS', totalSegments ?? 0);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async logsFilters({ commit, dispatch }) {
    try {
      const response$ = await MessagesManagementService.logsFilters();
      commit('UPDATE_LOGS_FILTERS', response$.data);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
    }
  },
  async exportLogs({ dispatch }, payload: { query: string; fileName: string }) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = await MessagesManagementService.exportLogs(payload.query);
      dispatch('isLoading', false, { root: true });
      const blob = new Blob([response$.data], { type: 'text/csv' });
      const anchor = document.createElement('a');
      anchor.download = payload.fileName;
      anchor.href = URL.createObjectURL(blob);
      anchor.click();
      URL.revokeObjectURL(anchor.href);
    } catch (e) {
      dispatch('snackBarMessage', isNetworkError(e) ? e?.message : e.response?.data?.message, { root: true });
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
};

const getters: GetterTree<StoreState<IBulkBatch>, RootState> = {
  getQueueStats: state => state.stats,
  getJobs: state => state.jobs,
  getJobsPagination: state => state.jobsPaginate,
  getAllBatches: state => state.all,
  getPagination: state => state.paginate,
  getChunks: state => state.chunks,
  getChunksPagination: state => state.chunksPaginate,
  getLogs: state => state.logs,
  getLogsPagination: state => state.logsPaginate,
  getLogsTotalSegments: state => state.logsTotalSegments,
  getLogsFilters: state => state.logsFilters,
};

export const messagesManagement: Module<StoreState<IBulkBatch>, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions: { ...actions, ...logsActions },
  getters,
};
